<!-- eslint-disable vue/no-v-html -->
<template>
  <div v-if="data.page">
    <CBreadcrumbs :breadcrumb="breadcrumb" base-path="/" />
    <TheHero
      v-if="hero"
      :title="hero.title"
      :description="hero.description"
      :background-url-desktop="hero.heroUrlDesktop"
      :background-url-mobile="hero.heroUrlMobile"
      :icon-url="hero.iconUrl"
      :overlay="hero.overlay"
    />
    <CDropdown
      v-if="navBarItems?.length"
      class="m-4 md:hidden"
      :options="navBarItems.map((item) => ({ label: item.text, value: item.to }))"
      :label="t('shortcuts')"
      :value="''"
      @update:model-value="navigateTo($event)"
    />
    <div class="flex w-full grow">
      <aside v-if="navBarItems.length" class="w-1/5 min-w-[200px] bg-white hidden md:block">
        <CHeading :level="4" class="mt-6 mb-2 ml-6 text-primary-900">{{ t('shortcuts') }}</CHeading>
        <CNavigationList :nav-links="navBarItems" />
      </aside>
      <div class="w-full p-4 md:px-10 lg:pt-10">
        <div
          v-if="
            data.page.preTitle ||
            data.page.ingress ||
            data.page.bodyTextPrimary ||
            (!isBusinessAreaPage && data.page.title)
          "
          class="px-4 pt-4 text-gray-800 content-page-body md:w-full md:my-0 md:mx-auto md:pb-10 md:px-0"
        >
          <span v-if="data.page.preTitle" class="block mb-1 pre-title text-primary-900 md:mb-2">
            {{ data.page.preTitle }}
          </span>
          <h1
            v-if="!isBusinessAreaPage && data.page.title"
            data-test="entry-title"
            class="text-sm text-primary-900 md:text-2xl"
          >
            {{ data.page.title }}
          </h1>
          <div
            v-if="data.page.ingress"
            class="text-xs font-bold text-primary-900 md:text-lg"
            v-html="richTextToHtmlString(data.page.ingress.json, data.page.ingress.links)"
          />
          <div
            v-if="data.page.bodyTextPrimary"
            class="body-text"
            v-html="richTextToHtmlString(data.page.bodyTextPrimary.json, data.page.bodyTextPrimary.links)"
          />
        </div>

        <RequiresAuthenticationSection v-if="contentProtected" />
        <template v-else>
          <SectionComponent
            v-for="section in sectionsToShow"
            :id="section.sys.id!"
            :key="section.sys.id"
            :content-type="section.__typename"
            :section="section"
            :is-business-area-page="isBusinessAreaPage"
          />
          <CategorySection
            v-if="data.categories.length"
            :categories="data.categories"
            :section-title="data.page.categorySectionTitle"
            :section-icon="data.page.categorySectionIcon"
          />
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { useContentStore, useUserStore } from '@/stores';
import type { ContentPage, ContentPageSections } from '@/types/contentful';
import type { Breadcrumb } from '@/types/page';

const { path, params } = useRoute();
const { public: config } = useRuntimeConfig();
const { getContentPage } = useContentStore();
const { isLoggedIn } = storeToRefs(useUserStore());
const { $productService } = useNuxtApp();
const { t } = useI18n();

type Data = {
  page: ContentPage | null;
  categories: Breadcrumb[];
  linkedFrom: { slug: string; title: string } | null;
};

const defaultData: Data = {
  page: null,
  categories: [],
  linkedFrom: null,
};

const { data } = await useAsyncData(
  `contentPage-${path}`,
  async (): Promise<Data> => {
    try {
      const category = params.slug;
      let categories = [];

      const { data, error } = await getContentPage(params.contentSlug as string, category as string);

      if (!data) {
        throw new Error('No content data');
      }

      if (data.categories) {
        const { data: categoriesData, error: categoriesError } = (await $productService.fetchBreadcrumbsBatch(
          data.categories,
          true,
        )) as any;
        categories = categoriesError ? [] : categoriesData;
      }

      if (error) {
        throw error;
      }

      const linkedFrom = data?.linkedFrom?.contentListingPageCollection?.items[0];

      return { page: data, categories, linkedFrom };
    } catch (error: any) {
      throw showError({ statusCode: 404, message: `Failed to get content: ${params.slug}/${params.contentSlug}` });
    }
  },
  {
    default: () => defaultData,
  },
);

const isBusinessAreaPage = computed(() => data.value.linkedFrom?.slug === 'specialistomraden');

const hero = computed(() => {
  if (data.value.page?.hero) {
    return {
      ...data.value.page.hero,
      heroUrlDesktop: data.value.page.hero.heroDesktop?.url,
      heroUrlMobile: data.value.page.hero.heroMobile?.url,
      iconUrl: data.value.page.hero.icon?.url,
    };
  } else {
    return false;
  }
});

const navBarItems = computed(() => {
  const items: { text: string; to: string }[] = [];
  if (data.value.page?.showSidebar && data.value.page?.sectionsCollection?.items.length) {
    for (const item of data.value.page?.sectionsCollection.items) {
      if (!item?.title) continue;
      items.push({ text: item.title, to: `#${item.title}` });
    }
  }
  return items;
});

const breadcrumb = computed(() => {
  const { linkedFrom } = data.value;

  if (linkedFrom) {
    return {
      name: data.value.page?.title,
      parents: [
        {
          name: linkedFrom.title,
          slug: linkedFrom.slug,
        },
      ],
    };
  }

  return {
    name: data.value.page?.title,
    parents: [],
  };
});

const contentProtected = computed(() => data.value.page?.authenticatedOnly && !isLoggedIn.value);

const filteredSections = computed(() => data.value.page?.sectionsCollection?.items.filter(Boolean) || []);

const getMeta = () => ({
  title: data.value.page?.title,
  description: documentToPlainTextString(data.value.page?.ingress?.json) || data.value.page?.metaDescription,
  link: [{ rel: 'canonical', href: data.value.page?.canonicalLink ?? `${config.baseUrl}${path}` }],
});
useHead(getMeta());

const sectionsToShow = computed(() => {
  return filteredSections.value?.filter((section: ContentPageSections) => section) ?? [];
});

onMounted(() => {
  // re-init OneTrust to force load cookie policy
  // @ts-ignore
  if (window.OneTrust) {
    // Remove OneTrust consent SDK
    const otConsentSdk = document.getElementById('onetrust-consent-sdk');
    if (otConsentSdk) {
      otConsentSdk.remove();
    }

    // @ts-ignore
    window.OneTrust.Init();

    setTimeout(() => {
      // @ts-ignore
      window.OneTrust.LoadBanner();
      const toggleDisplay = document.getElementsByClassName('ot-sdk-show-settings');

      for (let i = 0; i < toggleDisplay.length; i++) {
        // @ts-ignore
        toggleDisplay[i].onclick = (event: any) => {
          event.stopImmediatePropagation();
          // @ts-ignore
          window.OneTrust.ToggleInfoDisplay();
        };
      }
    }, 1000);
  }
});
</script>

<script lang="ts">
export default defineComponent({
  name: 'ContentPage',
});
</script>

<style lang="scss">
.content-page-body {
  @apply md:max-w-screen-sm;
  p {
    @apply my-4 leading-4;

    @screen md {
      @apply my-6 leading-6;
    }
  }

  blockquote {
    @apply flex items-start pl-6 border-l border-solid border-gray-300;

    > p {
      @apply m-0;
    }

    &:before {
      content: '-';
      @apply pr-1;
    }
  }

  ol {
    @apply list-decimal px-4;
  }

  ul {
    @apply list-disc px-4;

    li > p {
      @apply m-0;
    }
  }

  .pre-title,
  .text-media__text {
    @apply text-xxs;

    @screen md {
      @apply text-xs;
    }

    p {
      @apply leading-4;
    }
  }

  .body-text {
    @apply text-xxs;

    @screen md {
      @apply text-sm;
    }
  }

  .text-media__image {
    @screen md {
      max-height: 180px;
    }
  }

  th > p,
  td > p {
    @apply m-0;
  }
}
</style>
